/**
 * Os itens do menu do topo, com as chaves reais do catálogo `Acessar`.
 *
 * As chaves foram levantadas do legado (`Sistema/Views/Shared/*.cshtml`), onde
 * o `painel.Acessar(chave)` já é quem decide o que aparece. Item sem chave é
 * sempre visível.
 *
 * Uma melhoria que sai de graça: o catálogo `Acessar` guarda um `grupo` por
 * chave e a tela de hoje ignora isso, mostrando tudo numa lista corrida. Aqui o
 * "Mais" já nasce agrupado.
 */

export type ItemDeMenu = {
  rotulo: string
  para: string
  /** Chave do `Acessar`. Sem chave, o item é público dentro do sistema. */
  chave?: string
  /** Ainda não construído nesta volta — aparece desligado, com o motivo. */
  emBreve?: boolean
}

export type GrupoDeMenu = {
  rotulo: string
  itens: ItemDeMenu[]
  /** Frase no lugar dos itens, quando o grupo ainda não tem o que oferecer. */
  aviso?: string
}

export const menuPrincipal: ItemDeMenu[] = [
  { rotulo: "Projetos", para: "/projetos", chave: "projeto" },
  // A fila de revisão é a única tela que atravessa todos os projetos: quem
  // revisa olha uma fila, não abre projeto por projeto procurando o que chegou.
  { rotulo: "Revisão", para: "/revisao", chave: "projeto_editar" },
  { rotulo: "Conteúdo", para: "/conteudo", chave: "conteudo" },
  { rotulo: "Relatórios", para: "/relatorios", chave: "relatorio" },
  // Sem chave de propósito: quem pede pelo botão de feedback é qualquer pessoa, e é ela
  // quem precisa ver se o pedido foi feito — e se alguém já pediu o mesmo. A fila de quem
  // resolve continua em "Feedbacks", no Mais.
  { rotulo: "Solicitações", para: "/solicitacoes" },
]

export const menuMais: GrupoDeMenu[] = [
  {
    rotulo: "Outros projetos",
    itens: [
      { rotulo: "Torques de aperto", para: "/torques", chave: "outros_projetos_torques" },
      { rotulo: "Planos de manutenção", para: "/planos", chave: "outros_projetos_planos_manutencao" },
      { rotulo: "Correias", para: "/correias", chave: "outros_projetos_correias" },
      { rotulo: "Relés e fusíveis", para: "/reles", chave: "outros_projetos_relesFusiveis_sem_pinout" },
      { rotulo: "Tabela de valores", para: "/valores", chave: "outros_projetos_tabela_valores" },
      // O acervo compartilhado de figuras de torque: 41 desenhos servindo 395
      // torques. Fica sob a chave de torques porque e o mesmo assunto -- quem
      // cadastra torque e quem cuida do acervo.
      { rotulo: "Acervo de figuras de torque", para: "/acervo-torques", chave: "outros_projetos_torques" },
      // O desenho do conector de cada nome de sistema. Cadastro global, e a
      // imagem mora no disco do sistema antigo -- a tela explica em vez de
      // mostrar quadro quebrado.
      //
      // **A chave era `outros_projetos_torques`, copiada.** Quem tinha permissao de
      // torques via conectores tambem, e quem tinha so conectores nao via nada. Nao
      // existe chave propria para conectores em `diagweb.acessar` (conferido), entao
      // vale a chave do grupo -- que e a verdade: e um item de Outros Projetos.
      { rotulo: "Conectores por sistema", para: "/conectores-sistema", chave: "outros_projetos" },
    ],
  },
  {
    rotulo: "Tabelas",
    itens: [
      { rotulo: "Fipe", para: "/fipe", chave: "fipe" },
      // Consultar a Fipe e arrumar a Fipe sao trabalhos diferentes: um e olhar,
      // o outro e dividir registro com barra e recalcular campo derivado.
      { rotulo: "Fipe — manutenção", para: "/fipe-manutencao", chave: "fipe" },
      { rotulo: "Fraga", para: "/fraga", chave: "Fraga" },
      { rotulo: "Estudo de mercado", para: "/estudo-mercado", chave: "EstudoMercado" },
      // A carga da base de abrangencia. Substitui a tela de colar SQL do legado,
      // e pode apagar a base inteira -- por isso mora separada da consulta.
      { rotulo: "Abrangência — importar", para: "/abrangencia-importar", chave: "EstudoMercado" },
    ],
  },
  {
    rotulo: "Cadastros globais",
    itens: [
      // Valem para todos os projetos, e por isso não moram numa aba de projeto.
      { rotulo: "Cadastros do conteúdo", para: "/cadastros-conteudo", chave: "conteudo_categorias" },
      // Onde falta conteudo, por modelo e por sistema. Atravessa projeto e
      // produto, entao nao mora em aba de projeto.
      { rotulo: "Cobertura de conteúdo", para: "/cobertura", chave: "conteudo" },
  { rotulo: "Sistemas e fabricantes", para: "/catalogos", chave: "projeto_editar" },
      { rotulo: "Cores de função", para: "/cores-funcao", chave: "projeto_editar" },
      // A biblioteca de nomes de componente e os posicionamentos padrao. Os dois
      // valem para todos os projetos: um nome novo na biblioteca aparece no
      // desenho de qualquer um.
      { rotulo: "Catálogos do diagrama", para: "/catalogos-diagrama", chave: "projeto_editar" },
      // O catalogo dos 2.745 desenhos internos. E global: um desenho serve a
      // qualquer diagrama, e alterar um muda o traco em todos que o usam.
      { rotulo: "Desenhos internos", para: "/desenhos-internos", chave: "projeto_diagrama_interno" },
      // Os 64.631 componentes de todos os 921 projetos, para levar em planilha.
      // E o "Componentes Exportar" do menu antigo, que so existia para exportar.
      { rotulo: "Componentes — exportar", para: "/componentes", chave: "projeto_listar" },
    ],
  },
  {
    // Trazido do PainelAdm: o Sistema nunca teve como ver a licenca de um cliente.
    // "Clientes" e a ficha de posse; "Licencas" e o catalogo de planos.
    rotulo: "Clientes e licenças",
    itens: [
      { rotulo: "Clientes", para: "/clientes", chave: "cliente_ver" },
      { rotulo: "Licenças", para: "/licencas", chave: "cliente_ver" },
    ],
  },

  {
    rotulo: "Administração",
    itens: [
      { rotulo: "Usuários", para: "/usuarios", chave: "usuario_listar" },
      // Os dois feedbacks numa tela só: o dos clientes do site e o da equipe,
      // que chega pelo botão flutuante do próprio Sistema.
      { rotulo: "Feedbacks", para: "/feedback", chave: "relatorio" },
    ],
  },
]

/**
 * As abas do projeto aberto: só onde se cadastra o conteúdo.
 *
 * Eram treze, e a produção pediu (relatório de 28/09/2026) que a barra ficasse
 * com as de trabalho diário. Projeto, Cadastros, Sistemas, Modelos e Checklist
 * foram para o "⋯" no fim da barra (ver `maisDoProjeto`); Comparar e Catálogo
 * saíram do sistema, a pedido da produção.
 */
export const abasDoProjeto: ItemDeMenu[] = [
  { rotulo: "Pinout", para: "pinout", chave: "projeto_editar" },
  { rotulo: "Pinout interno", para: "pinout-interno", chave: "projeto_diagrama_interno" },
  { rotulo: "Gráfico", para: "grafico", chave: "projeto_editar" },
  { rotulo: "Importar", para: "importar", chave: "projeto_editar" },
  { rotulo: "Fusíveis", para: "fusiveis", chave: "projeto_editar" },
  { rotulo: "Diagramas", para: "diagramas", chave: "projeto_editar" },
]

/**
 * As telas do projeto que ficam no "⋯" do fim da barra de abas (ver
 * `telas/projeto/MaisDoProjeto.tsx`). `para` é o trecho depois de `/projetos/:id/`.
 */
export const maisDoProjeto: ItemDeMenu[] = [
  { rotulo: "Projeto", para: "projeto", chave: "projeto_editar" },
  { rotulo: "Cadastros", para: "cadastros", chave: "projeto_editar" },
  { rotulo: "Sistemas", para: "sistemas", chave: "projeto_editar" },
  { rotulo: "Modelos", para: "modelos", chave: "projeto_editar" },
  { rotulo: "Checklist", para: "checklist", chave: "projeto_editar" },
]
